/**
 * Form Modal 状态管理 Hook
 *
 * 将三个项目中大量重复的「Modal + Form + Loading + Create/Edit」模式
 * 抽象为一个统一的 hook。
 *
 * 功能:
 * - 自动管理 modal 的 open / close 状态
 * - 区分 create 和 edit 模式
 * - 编辑时自动回填 form 数据 (支持 transform)
 * - 提交时根据模式自动调用 create / update API
 * - 内置 loading 状态 + 防重复提交
 * - 成功 / 失败消息自动处理
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ANIMATION_DURATION } from '../constants/ui';
import type { ApiResponse } from '../types/api';
import { isApiSuccess } from '../utils/api-helpers';
import { handleOperationError } from './shared-async';
import { useLatestRef } from './useLatestRef';

// ===== 类型定义 =====

/**
 * 表单模式
 */
export type FormModalMode = 'create' | 'edit';

/**
 * FormModal 文案配置（支持国际化）
 */
export interface FormModalMessages {
  /** 创建模式标题，默认 'Create' */
  createTitle: string;
  /** 编辑模式标题，默认 'Edit' */
  editTitle: string;
  /** 创建成功提示，默认 'Created successfully' */
  createSuccess: string;
  /** 更新成功提示，默认 'Updated successfully' */
  updateSuccess: string;
  /** 操作失败提示，默认 'Operation failed' */
  operationFailed: string;
}

const DEFAULT_FORM_MODAL_MESSAGES: FormModalMessages = {
  createTitle: 'Create',
  editTitle: 'Edit',
  createSuccess: 'Created successfully',
  updateSuccess: 'Updated successfully',
  operationFailed: 'Operation failed',
};

/**
 * useFormModal 配置
 */
export interface UseFormModalOptions<
  FormValues extends Record<string, unknown> = Record<string, unknown>,
  RecordType = FormValues,
  CreateResult = unknown,
  UpdateResult = unknown,
> {
  /** Ant Design Form 实例 */
  form: {
    setFieldsValue: (values: Partial<FormValues>) => void;
    resetFields: () => void;
    validateFields: () => Promise<FormValues>;
  };

  /** 创建 API */
  createApi?: (data: FormValues) => Promise<ApiResponse<CreateResult>>;

  /** 更新 API */
  updateApi?: (data: FormValues) => Promise<ApiResponse<UpdateResult>>;

  /** 成功状态码, 默认 '200' */
  successCode?: string | number;

  /**
   * 将记录数据转换为表单初始值
   * (编辑模式: record -> form values)
   */
  transformToForm?: (record: RecordType) => Partial<FormValues>;

  /**
   * 将表单值转换为提交数据
   * (提交时: form values -> API params)
   */
  transformToSubmit?: (
    values: FormValues,
    mode: FormModalMode,
    record: RecordType | null,
  ) => FormValues;

  /** 自定义文案（支持国际化） */
  messages?: Partial<FormModalMessages>;

  /** 消息展示函数 */
  showMessage?: (content: string, type: 'success' | 'error') => void;

  /** 提交成功回调 */
  onSuccess?: (
    mode: FormModalMode,
    result: CreateResult | UpdateResult,
  ) => void;

  /** 提交失败回调 */
  onError?: (error: Error) => void;

  /** modal 关闭时是否重置表单, 默认 true */
  resetOnClose?: boolean;
}

/**
 * useFormModal 返回值
 */
export interface UseFormModalReturn<RecordType = Record<string, unknown>> {
  /** modal 是否可见 */
  visible: boolean;
  /** 当前模式 */
  mode: FormModalMode;
  /** 当前编辑的记录数据 (create 模式为 null) */
  record: RecordType | null;
  /** 提交中状态 */
  loading: boolean;
  /** 模态框标题 (根据 mode 自动生成 'Create' / 'Edit') */
  title: string;
  /** 打开 modal — 创建模式 */
  openCreate: () => void;
  /** 打开 modal — 编辑模式 */
  openEdit: (record: RecordType) => void;
  /** 关闭 modal */
  close: () => void;
  /** 执行提交 (校验表单 + 调 API) */
  submit: () => Promise<boolean>;
  /**
   * 展开到 Modal 的 props
   *
   * @example
   * ```tsx
   * <Modal {...modalProps}>
   *   <Form form={form}>...</Form>
   * </Modal>
   * ```
   */
  modalProps: {
    open: boolean;
    title: string;
    confirmLoading: boolean;
    onCancel: () => void;
    onOk: () => Promise<boolean>;
    destroyOnClose: boolean;
  };
}

// ===== Hook 实现 =====

/**
 * Form Modal 状态管理 Hook
 *
 * @example
 * ```tsx
 * const [form] = Form.useForm();
 *
 * const {
 *   modalProps,
 *   openCreate,
 *   openEdit,
 * } = useFormModal({
 *   form,
 *   createApi: createUser,
 *   updateApi: updateUser,
 *   showMessage: (content, type) => message[type](content),
 *   onSuccess: () => actionRef.current?.reload(),
 * });
 *
 * // 工具栏
 * <Button onClick={openCreate}>新增</Button>
 *
 * // 行操作
 * <a onClick={() => openEdit(record)}>编辑</a>
 *
 * // 弹窗
 * <Modal {...modalProps}>
 *   <Form form={form} layout="vertical">
 *     <Form.Item name="name" label="姓名" rules={[{ required: true }]}>
 *       <Input />
 *     </Form.Item>
 *   </Form>
 * </Modal>
 * ```
 *
 * @example
 * ```tsx
 * // 带数据转换
 * const { modalProps, openEdit } = useFormModal({
 *   form,
 *   updateApi: updateUser,
 *   transformToForm: (record) => ({
 *     ...record,
 *     birthday: dayjs(record.birthday),
 *   }),
 *   transformToSubmit: (values) => ({
 *     ...values,
 *     birthday: values.birthday?.format('YYYY-MM-DD'),
 *   }),
 *   showMessage: (content, type) => message[type](content),
 *   onSuccess: () => reload(),
 * });
 * ```
 */
export function useFormModal<
  FormValues extends Record<string, unknown> = Record<string, unknown>,
  RecordType = FormValues,
  CreateResult = unknown,
  UpdateResult = unknown,
>(
  options: UseFormModalOptions<
    FormValues,
    RecordType,
    CreateResult,
    UpdateResult
  >,
): UseFormModalReturn<RecordType> {
  const { form, successCode = '200', resetOnClose = true } = options;

  const msg: FormModalMessages = {
    ...DEFAULT_FORM_MODAL_MESSAGES,
    ...options.messages,
  };

  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<FormModalMode>('create');
  const [record, setRecord] = useState<RecordType | null>(null);
  const [loading, setLoading] = useState(false);

  // 防重复提交
  const submittingRef = useRef(false);

  // 用 useLatestRef 稳定回调引用 (避免 inline 函数导致 useCallback 频繁更新)
  const createApiRef = useLatestRef(options.createApi);
  const updateApiRef = useLatestRef(options.updateApi);
  const showMessageRef = useLatestRef(options.showMessage);
  const transformToFormRef = useLatestRef(options.transformToForm);
  const transformToSubmitRef = useLatestRef(options.transformToSubmit);
  const onSuccessRef = useLatestRef(options.onSuccess);
  const onErrorRef = useLatestRef(options.onError);

  // 关闭时清理
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // --- 打开: 创建模式 ---
  const openCreate = useCallback(() => {
    clearTimeout(timerRef.current);
    setMode('create');
    setRecord(null);
    form.resetFields();
    setVisible(true);
  }, [form]);

  // --- 打开: 编辑模式 ---
  const openEdit = useCallback(
    (editRecord: RecordType) => {
      clearTimeout(timerRef.current);
      setMode('edit');
      setRecord(editRecord);

      // 回填表单数据
      const formValues = transformToFormRef.current
        ? transformToFormRef.current(editRecord)
        : (editRecord as unknown as Partial<FormValues>);

      // 先重置再填充，避免残留字段（antd Form store 同步更新，无需 setTimeout）
      form.resetFields();
      form.setFieldsValue(formValues);

      setVisible(true);
    },
    [form, transformToFormRef],
  );

  // --- 关闭 ---
  const close = useCallback(() => {
    setVisible(false);
    setLoading(false);
    submittingRef.current = false;

    if (resetOnClose) {
      timerRef.current = setTimeout(() => {
        form.resetFields();
        setRecord(null);
      }, ANIMATION_DURATION.NORMAL); // 等待关闭动画
    }
  }, [form, resetOnClose]);

  // --- 提交 ---
  const submit = useCallback(async (): Promise<boolean> => {
    if (submittingRef.current) return false;

    const createApi = createApiRef.current;
    const updateApi = updateApiRef.current;

    // 前置检查 API 可用性 (在表单校验之前)
    if (mode === 'create' && !createApi) {
      throw new Error('createApi is not provided');
    }
    if (mode === 'edit' && !updateApi) {
      throw new Error('updateApi is not provided');
    }

    try {
      submittingRef.current = true;
      setLoading(true);

      // 校验表单
      const values = await form.validateFields();

      // 转换提交数据
      const submitData = transformToSubmitRef.current
        ? transformToSubmitRef.current(values, mode, record)
        : values;

      // 调用 API
      let response: ApiResponse<CreateResult | UpdateResult>;
      if (mode === 'create') {
        if (!createApi) {
          throw new Error('createApi is not provided');
        }
        response = await createApi(submitData);
      } else {
        if (!updateApi) {
          throw new Error('updateApi is not provided');
        }
        response = await updateApi(submitData);
      }

      const isSuccess = isApiSuccess(response, successCode);

      if (isSuccess) {
        const successMsg =
          mode === 'create' ? msg.createSuccess : msg.updateSuccess;
        showMessageRef.current?.(successMsg, 'success');
        onSuccessRef.current?.(
          mode,
          response.data as CreateResult | UpdateResult,
        );
        close();
        return true;
      }

      const errorMessage =
        response.msg ||
        response.message ||
        response.errorMessage ||
        msg.operationFailed;
      showMessageRef.current?.(errorMessage, 'error');
      return false;
    } catch (error) {
      // 表单校验失败不算错误
      if (error && typeof error === 'object' && 'errorFields' in error) {
        return false;
      }
      handleOperationError({
        error,
        fallbackMessage: msg.operationFailed,
        showError: (message) => showMessageRef.current?.(message, 'error'),
        onError: onErrorRef.current,
      });
      return false;
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  }, [
    form,
    mode,
    record,
    successCode,
    msg.createSuccess,
    msg.updateSuccess,
    msg.operationFailed,
    close,
    createApiRef,
    onErrorRef,
    onSuccessRef,
    showMessageRef,
    transformToSubmitRef,
    updateApiRef,
  ]);

  // --- 计算标题 ---
  const title = mode === 'create' ? msg.createTitle : msg.editTitle;

  // --- modalProps (memoized) ---
  const modalProps = useMemo(
    () => ({
      open: visible,
      title,
      confirmLoading: loading,
      onCancel: close,
      onOk: submit,
      destroyOnClose: true as const,
    }),
    [visible, title, loading, close, submit],
  );

  return {
    visible,
    mode,
    record,
    loading,
    title,
    openCreate,
    openEdit,
    close,
    submit,
    modalProps,
  };
}
